import React from 'react';
import cx from 'classnames';
import Logo from './Logo';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CustomerIcon, ExpensesIcon, HomeIcon, InvoiceIcon, SettingIcon, LogoutIcon, ReportIcon, InventoryIcon } from '../assets/icons';
import userPool from '../utils/userPool';
import { STORAGE_REFRESH_TOKEN, STORAGE_TOKEN, USERNAME } from '../constants';
import toggleMenu from '../../src/assets/icons/svgicons/toggleMenu.svg';

interface SideMenuProps {
  collapsed: boolean;
}

const SideMenu: React.FC<SideMenuProps> = ({ collapsed }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [isHidden, setIsHidden] = React.useState(false);

  const menuItems = [
    {
      paths: ['/dashboard'],
      label: 'Home',
      icon: <HomeIcon />,
      to: '/dashboard',
    },
    {
      paths: ['/invoices', '/invoices/create', '/invoices/:invoiceNo/view'],
      label: 'Invoices',
      icon: <InvoiceIcon />,
      to: '/invoices',
    },
    {
      paths: ['/expenses', '/expenses/create', '/expenses/expenseName/view'],
      label: 'Expenses',
      icon: <ExpensesIcon />,
      to: '/expenses',
    },
   
    {
      paths: ['/customers', '/customers/create', '/customers/customerName/view'],
      label: 'Customers',
      icon: <CustomerIcon />,
      to: '/customers',
    },
    {
      paths: ['/settings'],
      label: 'Settings',
      icon: <SettingIcon />,
      to: '/settings',
    }, 
   
    {
      paths: ['/inventories'],
      label: 'Inventory',
      icon: <InventoryIcon />,
      to: '/inventories',
    },
    {
      paths: ['/Reports',],
      label: 'Reports',
      icon: <ReportIcon />,
      to: '/reports',
    },
    {
      paths: ['/auth'], 
      label: 'Logout',
      icon: <LogoutIcon />,
      to: '/auth',
    },
  ];

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleLogout = () => {
    const user = userPool.getCurrentUser();
    if (user) {
      user.signOut();
      sessionStorage.removeItem(STORAGE_TOKEN);
      sessionStorage.removeItem(STORAGE_REFRESH_TOKEN);
      sessionStorage.removeItem(USERNAME);
      navigate('/auth');
    } else {
      console.error('No user found in the current session.');
    }
  };

  const isActive = (paths: string[]) => {
    return paths.some((path) => pathname.startsWith(path));
  };

 
  const renderMenuItem = (menuItem: any) => {
    if (menuItem.label === 'Logout') {
      return (
        <li key={menuItem.to} className="group mb-3 mt-4">
          <Link
            to={menuItem.to}
            onClick={handleLogout} 
            className={cx({
              'h-[55px] mt-[4em] flex items-center': true,
              'w-[212px]': !isCollapsed,
              'w-[59px]': isCollapsed,
              "before:content-[''] before:w-[10px] before:bg-[#FF6702]": isActive(menuItem.paths),
              'before:rounded-l-[10px] before:h-[55px] bg-[#C9EBF3]': isActive(menuItem.paths),
              'before:relative before:left-[-5px] rounded-r-[5px]': isActive(menuItem.paths),
              "hover:before:content-[''] hover:before:w-[10px] ": true,
              'hover:before:rounded-l-[10px] hover:before:h-[55px] mr-[10px] ml-[20px] hover:bg-[#C9EBF3]': true,
              'hover:before:relative hover:before:left-[-5px] hover:rounded-r-[5px] mr-[10px]': true,
              'text-[#222222]': isActive(menuItem.paths),
              'text-white': !isActive(menuItem.paths),
              'hover:text-[#222222]': true,
              collapsed: isCollapsed,
            })}
          >
            <div className="flex items-center justify-between w-[59px] h-full px-4">
              <div className="flex items-center space-x-4">
                <span className="text-lg font-light">
                  {menuItem.icon}
                  {isCollapsed && (
                    <span
                      className={cx({
                        'hidden group-hover:block font-medium text-base': true,
                        'text-[10px] flex ml-[-7px]': true,
                      })}
                    >
                      {menuItem.label}
                    </span>
                  )}
                </span>
                {!isCollapsed && (
                  <span className="font-medium text-base">{menuItem.label}</span>
                )}
              </div>
            </div>
          </Link>
        </li>
      );
    }
  
    // For other menu items, handle them as usual
    return (
      <li key={menuItem.to} className="group mb-3 mt-4">
        <Link
          to={menuItem.to}
          className={cx({
            'h-[55px] flex items-center': true,
            'w-[212px]': !isCollapsed,
            'w-[59px]': isCollapsed,
            "before:content-[''] before:w-[10px] before:bg-[#FF6702]": isActive(menuItem.paths),
            'before:rounded-l-[10px] before:h-[55px] bg-[#C9EBF3]': isActive(menuItem.paths),
            'before:relative before:left-[-5px] rounded-r-[5px]': isActive(menuItem.paths),
            "hover:before:content-[''] hover:before:w-[10px] ": true,
            'hover:before:rounded-l-[10px] hover:before:h-[55px] mr-[10px] ml-[20px] hover:bg-[#C9EBF3]': true,
            'hover:before:relative hover:before:left-[-5px] hover:rounded-r-[5px] mr-[10px]': true,
            'text-[#222222]': isActive(menuItem.paths),
            'text-white': !isActive(menuItem.paths),
            'hover:text-[#222222]': true,
            collapsed: isCollapsed,
          })}
        >
          <div className="flex items-center justify-between w-[59px] h-full px-4">
            <div className="flex items-center space-x-4">
              <span className="text-lg font-light">
                {menuItem.icon}
                {isCollapsed && (
                  <span
                    className={cx({
                      'hidden group-hover:block font-medium text-base': true,
                      'text-[10px] flex ml-[-7px]': true,
                    })}
                  >
                    {menuItem.label}
                  </span>
                )}
              </span>
              {!isCollapsed && (
                <span className="font-medium text-base">{menuItem.label}</span>
              )}
            </div>
          </div>
        </Link>
      </li>
    );
  };
  
  React.useEffect(() => {
    setIsCollapsed(collapsed);
  }, [collapsed]);

  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 991) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }
    };
    handleResize(); 
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  if (isHidden) {
    return null;
  }

  return (
    <aside
    className={cx('bg-[#0A78B2] hidden  lg:block relative', {
        'w-[92px]': isCollapsed,
        'w-[250px]': !isCollapsed,
      })}
    >
      <div className="flex items-center justify-between p-4 ">
        <Logo />
      </div>

      <nav>
        <ul>{menuItems.map((menuItem) => renderMenuItem(menuItem))}</ul>
      </nav>

      <button
        onClick={toggleSidebar}
        className={`right-[-15px]  pl-4 mt-[-1em] hidden lg:block absolute cursor-pointer text-[#0A79B3] `}
      >
        <img src={toggleMenu} alt="toggle" />
      </button>
    </aside>
  );
};

export default SideMenu;
